// PDF style primitives — the ONE implementation of the 2b visual standard
// (docs/pdf-redesign/padrao-visual.md + referencia-2b.html). Every generator
// draws its chrome (band, meta strip, sections, fields, checkboxes,
// signatures, footer) through these helpers.
//
// Multi-tenancy: the ONLY tenant-customisable slot is the colour of the header
// band (brand.primaryRgb). Everything else — the yellow fillet, the section
// ticks, the wordmark, the footer signature — is fixed Ordinex.
//
// All geometry is in mm (jsPDF unit used across the app) and was measured off
// referencia-2b.html in the browser: the reference page is 816px wide = 215.9mm
// (Letter), so px * 0.2646 = mm and pt = px * 0.75.
//
// The `ctx` object returned by startDoc carries the y cursor and page state so
// primitives can auto-break pages (and re-open the current section with a
// "(cont.)" bar).

import { T, BADGE, FONT, tenantScheme, flatten } from './tokens.js';

export const LAYOUT = {
  // 44px of side padding in the reference = 11.64mm. Callers that pass an
  // explicit margin keep theirs.
  MARGIN: 11.6,
  MARGIN_CONTRACT: 20,
  MARGIN_BOTTOM: 18,
};

// ── 2b page-chrome geometry (mm) ────────────────────────────────────────────
const BAND_H       = 26;    // full-bleed tenant band, portrait
const BAND_H_SLIM  = 14;    // landscape variant
const FILLET_H     = 0.8;   // 3px Ordinex yellow fillet under the band
const LOGO_H       = 12.7;  // 48px logo slot
const LOGO_GAP     = 4.2;   // 16px between the logo chip and the text block
const META_PAD_Y   = 3.1;   // half the strip's 6.2mm vertical padding budget
const META_GAP     = 5.3;   // 20px grid gap
const META_ROW_H   = 7;     // one label-over-value row
const META_LINE_H  = 4.35;  // each extra wrapped value line — a hair over the
                            // 4.30mm the 9.75pt/1.25 value text actually draws
                            // at, so a wrapped meta value never crowds the row
                            // below (the old 4 reserved less than was drawn)
const FOOTER_RULE  = 12;    // hairline sits this far above the page bottom

// jsPDF's addImage mis-decodes when told the wrong format; tenant logos and
// SignaturePad exports can be either.
export function imageFormat(dataUrl) {
  return /^data:image\/jpe?g/i.test(dataUrl || '') ? 'JPEG' : 'PNG';
}

// Structure audit for the redesign protocol: counts what kind of widget got
// drawn so baseline vs redesigned output can be compared mechanically.
// Inert unless the harness sets window.__pdfAudit = {}.
function audit(kind, n = 1) {
  if (typeof window !== 'undefined' && window.__pdfAudit)
    window.__pdfAudit[kind] = (window.__pdfAudit[kind] || 0) + n;
}

// The five type roles of the standard, mapped onto the IBM Plex faces
// registered by js/vendor/plex-fonts.js.
const PLEX_STYLE = {
  normal: [FONT.sans, 'normal'],
  bold:   [FONT.sans, 'bold'],
  semi:   [FONT.semi, 'normal'],
  mono:   [FONT.mono, 'normal'],
  italic: [FONT.sans, 'normal'],   // no italic face is subsetted
};
// A PDF must never fail to generate because a font file did not load (the
// field app runs offline): fall back to the jsPDF built-ins, which are always
// present. Semi-bold has no built-in equivalent — bold is the closer read.
const CORE_STYLE = {
  normal: ['helvetica', 'normal'],
  bold:   ['helvetica', 'bold'],
  semi:   ['helvetica', 'bold'],
  mono:   ['courier', 'normal'],
  italic: ['helvetica', 'italic'],
};

export function setF(doc, size, style, color) {
  if (doc.__plexOk === undefined) {
    try { doc.__plexOk = !!doc.getFontList()[FONT.sans]; }
    catch { doc.__plexOk = false; }
  }
  const key = PLEX_STYLE[style] ? style : 'normal';
  const [family, variant] = (doc.__plexOk ? PLEX_STYLE : CORE_STYLE)[key];
  try { doc.setFont(family, variant); }
  catch { doc.setFont('helvetica', variant === 'bold' ? 'bold' : 'normal'); }
  doc.setFontSize(size);
  if (color) doc.setTextColor(...color);
}

function hline(doc, x, y, w, weight, color) {
  doc.setDrawColor(...color);
  doc.setLineWidth(weight);
  doc.line(x, y, x + w, y);
}

// Width of a tracked-out string. jsPDF applies charSpace after every glyph
// including the last; for right alignment we want the last glyph's edge on the
// margin, so the trailing slot is excluded.
function trackedW(doc, s, cs) {
  const str = String(s);
  return doc.getTextWidth(str) + (cs ? cs * Math.max(0, str.length - 1) : 0);
}

function tracked(doc, s, x, y, cs) {
  if (cs) doc.text(String(s), x, y, { charSpace: cs });
  else doc.text(String(s), x, y);
}

// Word-wraps a string that will be drawn tracked (charSpace'd). Plain
// splitTextToSize measures glyph widths only — it does not know about
// charSpace — so it under-wraps a tracked string and lets the tail run off
// the page. A real caller hit this: commentArea's field-instruction label in
// the Incident Statement generator is a full sentence, and at 7.1pt mono with
// 0.26mm tracking its tail (" How? Why?") measured past the page edge and
// silently never got drawn. One word per line in the pathological case where
// even a single word does not fit — still no truncation.
// trackedW excludes the trailing glyph's charSpace slot (it was written for
// right-alignment); that makes a fit check here ~0.26mm optimistic per line
// at this tracking, i.e. it may pack a word that overflows by a hair less
// than one charSpace unit — immaterial at these font sizes and column widths.
function trackedSplit(doc, s, maxW, cs) {
  const words = String(s).split(/\s+/).filter(Boolean);
  if (!words.length) return [''];
  const lines = [];
  let line = '';
  words.forEach((w) => {
    const candidate = line ? line + ' ' + w : w;
    if (line && trackedW(doc, candidate, cs) > maxW) {
      lines.push(line);
      line = w;
    } else {
      line = candidate;
    }
  });
  if (line) lines.push(line);
  return lines;
}

// Baseline-to-baseline spacing of an extra wrapped mono-caps label line (7.1pt
// PlexMono), shared by fieldRow and commentArea so a wrapped field label and
// a wrapped comment label grow at the same rate.
const LABEL_LINE_PITCH = 3.4;

// Two-letter monogram from the tenant name — the letterhead mark when the org
// has not uploaded a logo ("Carioca Ltd" -> "CL").
function monogram(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  return words.slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

// Measures the band's logo chip and hands back a renderer, so the band height
// can be settled before anything is drawn.
function prepLogo(doc, brand, scheme, h) {
  const padX = 2.65, radius = 1.6;
  let img = brand.logoDataUrl || null;
  let iw = 0, ih = 0;
  if (img) {
    try {
      const p = doc.getImageProperties(img);
      const k = Math.min((h - 3.2) / p.height, 34 / p.width);   // 36px x 130px
      iw = p.width * k; ih = p.height * k;
    } catch { img = null; }
  }
  const mark = img ? '' : monogram(brand.name);
  if (!img && !mark) return { w: 0, render() {} };
  let w;
  if (img) {
    w = Math.min(39.7, Math.max(h, iw + padX * 2));
  } else {
    setF(doc, 11, 'mono', scheme.on);
    w = Math.max(h, doc.getTextWidth(mark) + padX * 2);
  }
  return {
    w,
    render(x, yTop) {
      // Plate behind a real tenant logo: always white (Renan, 2026-08-01 —
      // "placa branca somente, manter simples"). Logos are drawn for white
      // paper, so white is the one background that can't fight the artwork;
      // the band color underneath is irrelevant. Known limit, accepted: a
      // near-white artwork needs a dark/colored version uploaded instead.
      // The mark tile and the monogram keep the translucent chip (both are
      // designed for the band).
      doc.setFillColor(...(img && brand.logoDataUrl ? T.WHITE : scheme.logoBg));
      // referencia-2b.html:31 gives the logo slot its own border alpha (.18),
      // a touch stronger than the date chip's .14 — computed here rather than
      // in tenantScheme so the token contract keeps its existing keys.
      doc.setDrawColor(...flatten(scheme.band, scheme.on, 0.18));
      doc.setLineWidth(0.2);
      doc.roundedRect(x, yTop, w, h, radius, radius, 'FD');
      if (img) {
        try {
          doc.addImage(img, imageFormat(img),
            x + (w - iw) / 2, yTop + (h - ih) / 2, iw, ih);
        } catch { /* unreadable logo: the chip alone still reads as a mark */ }
      } else {
        setF(doc, 11, 'mono', scheme.on);
        doc.text(mark, x + w / 2, yTop + h / 2 + 1.6, { align: 'center' });
      }
    },
  };
}

// Meta entries flow into a grid of at most 5 cells per row, balanced so a
// 6-entry document reads 3 + 3 rather than 5 + 1.
function metaRows(entries, max) {
  const n = entries.length;
  if (!n) return [];
  const rows = Math.ceil(n / max);
  const per = Math.ceil(n / rows);
  const out = [];
  for (let i = 0; i < n; i += per) out.push(entries.slice(i, i + per));
  return out;
}

// Label-over-value strip under the band. Full-bleed fill, hairline at the
// bottom; rows grow with wrapped values. Returns its height.
function drawMetaStrip(doc, entries, x0, yTop, W, pageW) {
  const rows = metaRows(entries, 5);
  if (!rows.length) return 0;
  // Every row uses the same column count so the cells line up down the strip —
  // a short last row leaves its trailing columns empty rather than stretching.
  const cols = rows[0].length;
  const plan = rows.map((cells) => {
    const cw = (W - META_GAP * (cols - 1)) / cols;
    setF(doc, 9.75, 'semi', T.INK);
    const wrapped = cells.map((c) => {
      const raw = c[1];
      const v = raw === null || raw === undefined || String(raw).trim() === ''
        ? '—' : String(raw);
      return doc.splitTextToSize(v, cw);
    });
    const lines = Math.max(1, ...wrapped.map(l => l.length));
    return { cells, cw, wrapped, h: META_ROW_H + (lines - 1) * META_LINE_H };
  });
  const stripH = META_PAD_Y * 2 + plan.reduce((s, p) => s + p.h, 0);
  doc.setFillColor(...T.STRIP);
  doc.rect(0, yTop, pageW, stripH, 'F');
  hline(doc, 0, yTop + stripH, pageW, 0.2, T.HAIR);
  let y = yTop + META_PAD_Y;
  plan.forEach((p) => {
    p.cells.forEach((c, i) => {
      const cx = x0 + i * (p.cw + META_GAP);
      setF(doc, 7.1, 'mono', T.MUTED2);
      tracked(doc, String(c[0] ?? '').toUpperCase(), cx, y + 2.7, 0.26);
      setF(doc, 9.75, 'semi', T.INK);
      doc.text(p.wrapped[i], cx, y + 6.8, { lineHeightFactor: 1.25 });
    });
    y += p.h;
  });
  return stripH;
}


// Page-1 letterhead + centered title. opts:
//   title (required), subtitle, docId, meta [[label, value], ...],
//   margin (default LAYOUT.MARGIN), slim (landscape one-line variant).
// Returns the ctx every other primitive takes.
// jsPDF's built-in fonts (Helvetica & co.) are WinAnsi-encoded: anything
// outside that range is written as raw bytes and comes out as mojibake —
// "📋 Incident Details" printed as "Ø=ÜË Incident Details" on a real client
// document. The sources are unbounded (emoji in a schema's section label, an
// emoji a worker types into a comment, a pasted symbol in an org name), so
// this is enforced at the ONLY place text reaches the page instead of at each
// caller. WinAnsi = Latin-1 plus the printable slots in 0x80–0x9F.
const WINANSI_HIGH = '€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ';
// Symbols that carry meaning in a label get an ASCII stand-in rather than
// being dropped: "Batch Time → Placement Time" must not read as two unrelated
// words stuck together.
const TRANSLITERATE = {
  '→': '->', '←': '<-', '↔': '<->', '⇒': '=>', '⇐': '<=', '↑': '^', '↓': 'v',
  '≥': '>=', '≤': '<=', '≠': '!=', '≈': '~', '×': 'x', '÷': '/', '±': '+/-',
  '✓': 'Yes', '✔': 'Yes', '✗': 'No', '✘': 'No', '∅': '-', '·': '-',
  // The typographic minus (U+2212) is not WinAnsi: dropped, "−11%" printed as
  // a gain, "11%" (Production Report, 2026-10-16).
  '−': '-',
};
function toWinAnsi(s) {
  let out = '';
  for (const ch of String(s)) {
    if (TRANSLITERATE[ch] !== undefined) { out += TRANSLITERATE[ch]; continue; }
    const c = ch.codePointAt(0);
    if ((c >= 0x20 && c <= 0xFF) || ch === '\n' || ch === '\t' || WINANSI_HIGH.includes(ch)) out += ch;
  }
  // Dropping a leading icon leaves the separator behind ("📋 Details").
  return out.replace(/\s{2,}/g, ' ').trim();
}

// Wraps doc.text once per document. Idempotent: re-wrapping a doc that was
// already started (bespoke generators that call startDoc twice) is a no-op.
function guardText(doc) {
  if (doc.__winAnsiGuarded) return;
  doc.__winAnsiGuarded = true;
  const orig = doc.text.bind(doc);
  doc.text = function (txt, ...rest) {
    return orig(Array.isArray(txt) ? txt.map(toWinAnsi) : toWinAnsi(txt), ...rest);
  };
  // splitTextToSize measures the string it is given; feeding it the sanitized
  // text keeps wrap widths honest with what actually gets drawn.
  const origSplit = doc.splitTextToSize.bind(doc);
  doc.splitTextToSize = function (txt, ...rest) { return origSplit(toWinAnsi(txt), ...rest); };
}

// A 10-digit North American number as typed in Settings ("6044202158")
// prints as 604-420-2158; anything else is printed exactly as typed.
export function formatPhone(p) {
  const s = String(p ?? '').trim();
  const d = s.replace(/\D/g, '');
  const n = d.length === 11 && d[0] === '1' ? d.slice(1) : d;
  return n.length === 10 ? `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6)}` : s;
}

export function startDoc(doc, brand, opts) {
  guardText(doc);
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = opts.margin ?? LAYOUT.MARGIN;
  const W = pageW - margin * 2;
  const ctx = {
    doc, brand, margin, W, pageW, pageH,
    maxY: pageH - LAYOUT.MARGIN_BOTTOM,
    y: margin,
    title: opts.title,
    docId: opts.docId || '',
    _section: null,
  };
  ctx.headerLine = [brand.name, opts.title].filter(Boolean).join(' — ')
    + (ctx.docId ? ' · ' + ctx.docId : '');

  // The tenant's colour reaches exactly one thing: the band. Everything
  // derived from it (text, chips, wordmark) stays legible on any hue.
  const scheme = tenantScheme(brand.primaryRgb || T.INK);

  // The old letterhead printed the address/phone contact line. Keep that
  // content: it becomes meta cells — but never twice, and never overriding a
  // caller that already asked for those labels.
  const meta = (opts.meta || []).slice();
  const hasLabel = (l) => meta.some(m => String(m[0] ?? '').trim().toLowerCase() === l);
  // opts.contact === false: a letter to a client carries its own addressee
  // block — our address in that strip reads as the project's (CO, 2026-09-29).
  if (opts.contact !== false) {
    if (brand.address && !hasLabel('address')) meta.push(['Address', brand.address]);
    if (brand.phone && !hasLabel('phone')) meta.push(['Phone', formatPhone(brand.phone)]);
  }

  const eyebrow = String(opts.eyebrow ?? brand.name ?? '').toUpperCase();

  // The chip carries the DOCUMENT's date, never the day it was printed. A
  // tailgate finished the morning after the shift is the normal case, and a
  // safety record whose header contradicts its own Date field is worse than
  // useless in an audit. So: an explicit opts.docDate wins, otherwise the
  // caller's own 'Date' meta cell, otherwise no chip at all.
  const dateMeta = (opts.meta || [])
    .find(m => /^date$/i.test(String(m[0] ?? '').trim()));
  const chipRaw = opts.docDate ?? (dateMeta ? dateMeta[1] : undefined);
  const chipTxt = chipRaw === null || chipRaw === undefined ? '' : String(chipRaw).trim();
  // Callers pass '—' for an empty date; a chip reading "—" is noise.
  const dateChip = (chipTxt === '—' || chipTxt === '-') ? '' : chipTxt;

  if (opts.slim) {
    // ── Landscape: 14mm band, eyebrow + title on one line, meta right ──
    const logo = prepLogo(doc, brand, scheme, 8);
    const textX = margin + (logo.w ? logo.w + 3.2 : 0);

    // Tracking is deliberately tighter than the portrait eyebrow's 0.4mm: on
    // the slim band the eyebrow shares a baseline with the much larger title,
    // and PDF text extractors size their word-break threshold off the tallest
    // run on the line — at 0.4mm "CARIOCA LTD" came back out of pdftotext as
    // "C A R I O C A L T D", i.e. the tenant name stopped being searchable.
    setF(doc, 7.5, 'mono');
    const eyeW = eyebrow ? trackedW(doc, eyebrow, 0.15) : 0;
    const titleX = textX + (eyeW ? eyeW + 4.6 : 0);

    // The meta line used to be joined blind and ran off the page (and over the
    // logo) on a report with six entries. Measure and wrap it instead.
    setF(doc, 7.5, 'mono');
    const cells = meta.map(m => `${m[0]}: ${m[1] ?? '—'}`);
    // The meta column keeps at least 40mm: a title too long for what is left
    // shrinks, then is cut with an ellipsis, so the two never overprint.
    let title = String(opts.title || ''), titleSize = 11.5;
    const titleRoom = (margin + W) - titleX - (cells.length ? 6 + 40 : 0);
    setF(doc, titleSize, 'bold');
    while (titleSize > 8 && doc.getTextWidth(title) > titleRoom) { titleSize -= 0.5; setF(doc, titleSize, 'bold'); }
    if (doc.getTextWidth(title) > titleRoom) {
      while (title.length > 1 && doc.getTextWidth(title + '…') > titleRoom) title = title.slice(0, -1);
      title = title.trimEnd() + '…';
    }
    const titleW = doc.getTextWidth(title);
    const metaMaxW = Math.max(40, (margin + W) - (titleX + titleW + 6));
    setF(doc, 7.5, 'mono');
    const metaLines = [];
    cells.forEach((c) => {
      const last = metaLines.length - 1;
      const merged = last >= 0 ? metaLines[last] + '  ·  ' + c : c;
      if (last >= 0 && doc.getTextWidth(merged) <= metaMaxW) metaLines[last] = merged;
      // A cell wider than the column wraps inside it instead of running left.
      else metaLines.push(...doc.splitTextToSize(c, metaMaxW));
    });

    const bandH = Math.max(BAND_H_SLIM, 6 + metaLines.length * 3.2, 8 + 5);
    doc.setFillColor(...scheme.band);
    doc.rect(0, 0, pageW, bandH, 'F');
    doc.setFillColor(...T.ORDINEX);
    doc.rect(0, bandH, pageW, FILLET_H, 'F');

    logo.render(margin, (bandH - 8) / 2);
    const base = bandH / 2 + 1.5;
    if (eyebrow) {
      setF(doc, 7.5, 'mono', scheme.onMuted);
      tracked(doc, eyebrow, textX, base - 0.2, 0.15);
    }
    setF(doc, titleSize, 'bold', scheme.on);
    doc.text(title, titleX, base);

    setF(doc, 7.5, 'mono', scheme.onMuted);
    const metaTop = (bandH - metaLines.length * 3.2) / 2;
    metaLines.forEach((line, i) => {
      doc.text(line, margin + W, metaTop + 2.3 + i * 3.2, { align: 'right' });
    });

    ctx.y = bandH + FILLET_H + 6;
    return ctx;
  }

  // ── Portrait: full-bleed band + Ordinex fillet + meta strip ──
  const logo = prepLogo(doc, brand, scheme, LOGO_H);
  const textX = margin + (logo.w ? logo.w + LOGO_GAP : 0);

  // Right block (wordmark over date chip) is measured first: it decides how
  // much width the title has.
  setF(doc, 8.25, 'mono');
  const wordmarkW = trackedW(doc, 'ORDINEX', 0.66);
  setF(doc, 10.5, 'mono');
  const chipW = dateChip ? doc.getTextWidth(dateChip) + 5.3 : 0;
  const chipH = 6.6;
  const rightW = Math.max(wordmarkW, chipW);
  const availW = Math.max(30, pageW - margin - rightW - 6 - textX);

  // Long titles shrink, then wrap, and the band grows to hold them — a
  // document never loses its own name, and nothing ever runs under the
  // wordmark.
  let titleSize = 15.75;
  const rawTitle = String(opts.title || '');
  setF(doc, titleSize, 'bold');
  while (titleSize > 12 && doc.getTextWidth(rawTitle) > availW) {
    titleSize -= 0.25;
    setF(doc, titleSize, 'bold');
  }
  const titleLines = doc.getTextWidth(rawTitle) > availW
    ? doc.splitTextToSize(rawTitle, availW)
    : [rawTitle];
  const titleLineH = titleSize * 0.4233 * 1.18;

  // The subtitle gets the same width budget as the title, so a long document
  // type wraps inside the left block instead of running under the date chip.
  // It wraps rather than truncates — it is the only place that text appears.
  let subLines = [];
  if (opts.subtitle) {
    setF(doc, 9.75, 'normal');
    subLines = doc.splitTextToSize(String(opts.subtitle), availW);
  }

  const blockH = (eyebrow ? 3.4 : 0)
    + titleLines.length * titleLineH
    + subLines.length * 4.6;
  const bandH = Math.max(BAND_H, blockH + 11.5, LOGO_H + 9);

  doc.setFillColor(...scheme.band);
  doc.rect(0, 0, pageW, bandH, 'F');
  doc.setFillColor(...T.ORDINEX);
  doc.rect(0, bandH, pageW, FILLET_H, 'F');

  logo.render(margin, (bandH - LOGO_H) / 2);

  let ty = (bandH - blockH) / 2;
  if (eyebrow) {
    setF(doc, 7.9, 'mono', scheme.onMuted);
    tracked(doc, eyebrow, textX, ty + 2.55, 0.4);
    ty += 3.4;
  }
  setF(doc, titleSize, 'bold', scheme.on);
  titleLines.forEach((line, i) => {
    doc.text(line, textX, ty + titleLineH * (i + 1) - titleLineH * 0.27);
  });
  ty += titleLines.length * titleLineH;
  if (subLines.length) {
    setF(doc, 9.75, 'normal', scheme.onMuted);
    doc.text(subLines, textX, ty + 3.4, { lineHeightFactor: 1.34 });
  }

  // Without a chip the wordmark alone is the right block, and centring it on
  // the band keeps the header balanced instead of leaving it floating high.
  const rightBlockH = dateChip ? 3.63 + 2.65 + chipH : 3.63;
  const rightTop = (bandH - rightBlockH) / 2;
  setF(doc, 8.25, 'mono', scheme.wordmark);
  tracked(doc, 'ORDINEX', pageW - margin - wordmarkW, rightTop + 2.7, 0.66);
  if (dateChip) {
    doc.setFillColor(...scheme.chipBg);
    doc.setDrawColor(...scheme.chipBd);
    doc.setLineWidth(0.2);
    doc.roundedRect(pageW - margin - chipW, rightTop + 6.28, chipW, chipH, 1.1, 1.1, 'FD');
    setF(doc, 10.5, 'mono', scheme.on);
    doc.text(dateChip, pageW - margin - chipW + 2.65, rightTop + 6.28 + chipH / 2 + 1.35);
  }

  const stripH = drawMetaStrip(doc, meta, margin, bandH + FILLET_H, W, pageW);
  ctx.y = bandH + FILLET_H + stripH + 7;
  return ctx;
}

// Compact continuation header (pages 2+): page 1 carries the full band, every
// page after it carries this one line — tenant, document, date, hairline.
export function newPage(ctx) {
  const { doc } = ctx;
  doc.addPage();
  ctx.y = ctx.margin;
  setF(doc, 7, 'mono', T.MUTED);
  doc.text(ctx.headerLine, ctx.margin, ctx.y);
  const dateStr = ctx._headerDate || '';
  if (dateStr) doc.text(dateStr, ctx.margin + ctx.W, ctx.y, { align: 'right' });
  hline(doc, ctx.margin, ctx.y + 2, ctx.W, 0.2, T.HAIR);
  ctx.y += 7;
  if (ctx._section) sectionBar(ctx, ctx._section, { cont: true, _auto: true });
}

// Break the page if `h` mm won't fit above the footer reserve.
export function ensure(ctx, h) {
  if (ctx.y + h > ctx.maxY) newPage(ctx);
}

// Section title: yellow Ordinex tick + caps + hairline. No filled bar — the
// only solid colour on the paper is the band and the meta strip. Keeps the
// section name so automatic page breaks re-open it as "(cont.)".
export function sectionBar(ctx, title, opts = {}) {
  const { doc } = ctx;
  audit('section');
  if (!opts._auto) {
    ctx._section = null;          // the bar itself must not trigger a cont-bar
    // Never orphan a title at the page bottom. The 10mm default assumes the
    // first block under the bar is at least row-sized; a caller whose first
    // block is taller (a photo row is up to 64mm) passes its real height as
    // opts.keepWith so bar and block break to the next page together.
    ensure(ctx, 8.5 + Math.max(10, opts.keepWith || 0));
  }
  doc.setFillColor(...T.ORDINEX);
  doc.rect(ctx.margin, ctx.y + 1.15, 2.1, 2.1, 'F');
  setF(doc, 9, 'bold', T.INK);
  tracked(doc, String(title).toUpperCase() + (opts.cont ? ' (CONT.)' : ''),
    ctx.margin + 4.2, ctx.y + 3.3, 0.32);
  hline(doc, ctx.margin, ctx.y + 5.5, ctx.W, 0.2, T.HAIR);
  ctx.y += 8.5;
  ctx._section = title;
}

// Level-2 heading inside a section (checklist group title): plain semibold
// text, no box, no rule.
export function subsection(ctx, title) {
  const { doc } = ctx;
  audit('subsection');
  ensure(ctx, 6 + 8);
  setF(doc, 9.4, 'semi', T.INK);
  doc.text(String(title), ctx.margin, ctx.y + 3.4);
  ctx.y += 6;
}

// Close the current section: 6mm gap, stop cont-bars.
export function sectionEnd(ctx) {
  ctx._section = null;
  ctx.y += 6;
}

// One row of equal-width label-over-value cells: mono caps label over a
// semibold value, no cell borders — a single hairline closes the row (the
// 2b standard's field grammar, shared with the meta strip). Row grows with
// the tallest wrapped value (never truncate a filled value).
export function fieldRow(ctx, cells, minH = 10) {
  const { doc } = ctx;
  audit('field', cells.length);
  // A cell is [label, value] or [label, value, sx] where sx is the cell's
  // width in SIXTHS of the row (2 = 1/3, 3 = 1/2, 4 = 2/3). Rows with no sx
  // keep the historical equal split — every pre-fraction caller (meta strip,
  // timesheet, EWO) prints byte-identical. A partially filled fraction row
  // leaves the remainder blank instead of stretching (authored width is a
  // layout fact, not a hint).
  const hasSx = cells.some(c => c[2]);
  const widths = hasSx
    ? cells.map(c => ctx.W * (c[2] || 6 / cells.length) / 6)
    : cells.map(() => ctx.W / cells.length);
  setF(doc, 9.75, 'semi');
  const wrapped = cells.map((c, i) => {
    const v = c[1] === null || c[1] === undefined || c[1] === '' ? '—' : String(c[1]);
    return doc.splitTextToSize(v, widths[i] - 4);
  });
  const maxLines = Math.max(1, ...wrapped.map(l => l.length));
  setF(doc, 7.1, 'mono');
  // Tenant-authored form-builder labels (T8) are unbounded length. Wrap each
  // cell's label inside its own column — same width budget as the value — so
  // a long label wraps instead of running past its column or off the page.
  const labelWrapped = cells.map((c, i) =>
    trackedSplit(doc, String(c[0] ?? '').toUpperCase(), widths[i] - 4, 0.26));
  const maxLabelLines = Math.max(1, ...labelWrapped.map(l => l.length));
  const labelExtra = (maxLabelLines - 1) * LABEL_LINE_PITCH;
  // Same 4.2mm line pitch the old boxed row used for its value text; +7 is
  // the (single) label line and top/bottom breathing room the box border used
  // to imply; labelExtra adds room for any label lines beyond the first, so
  // wrapping the label never eats into the value's own wrap math.
  const h = Math.max(minH, labelExtra + maxLines * 4.2 + 7);
  ensure(ctx, h);
  let x = ctx.margin;
  cells.forEach((c, i) => {
    setF(doc, 7.1, 'mono', T.MUTED);
    labelWrapped[i].forEach((ln, j) =>
      tracked(doc, ln, x, ctx.y + 2.7 + j * LABEL_LINE_PITCH, 0.26));
    const empty = wrapped[i].length === 1 && wrapped[i][0] === '—';
    setF(doc, 9.75, 'semi', empty ? T.MUTED : T.INK);
    doc.text(wrapped[i], x, ctx.y + 6.8 + labelExtra, { lineHeightFactor: 1.25 });
    x += widths[i];
  });
  hline(doc, ctx.margin, ctx.y + h, ctx.W, 0.2, T.HAIR_LT);
  ctx.y += h;
}

// Full-width free-text cell: mono caps label over body copy, no box. Grows
// with content (never truncate a filled value).
// 4.9mm is 9.4pt's own line pitch at lineHeightFactor 1.5 — the same
// "value height + 7 for the label" shape fieldRow uses.
const COMMENT_LINE_H = 4.9;
export function commentArea(ctx, label, text, minH = 14) {
  const { doc } = ctx;
  audit('textField');
  const v = text === null || text === undefined || text === '' ? '—' : String(text);
  setF(doc, 9.4, 'normal');
  const lines = doc.splitTextToSize(v, ctx.W);
  setF(doc, 7.1, 'mono');
  // Most labels are one short caps word or two — this only wraps the rare
  // caller that hands commentArea a full sentence as its "label" (Incident
  // Statement's field instructions). Single-line case is unaffected below.
  const labelLines = trackedSplit(doc, String(label ?? '').toUpperCase(), ctx.W, 0.26);
  const labelExtra = (labelLines.length - 1) * LABEL_LINE_PITCH;
  const h = Math.max(minH, labelExtra + lines.length * COMMENT_LINE_H + 7);

  // A block taller than a whole page's writable area can never satisfy
  // ensure(): the one-shot path would draw every line past the footer reserve
  // straight off the paper. Flow those line by line across pages instead —
  // newPage() re-opens the section as "(CONT.)". Anything that fits one page
  // keeps the exact one-shot path below.
  const freshPageCap = ctx.maxY - (ctx.margin + 7 + 8.5);   // cont header + cont bar
  if (h > freshPageCap) {
    ensure(ctx, labelExtra + 6.8 + COMMENT_LINE_H * 2);     // label + ≥2 body lines together
    setF(doc, 7.1, 'mono', T.MUTED);
    labelLines.forEach((ln, i) =>
      tracked(doc, ln, ctx.margin, ctx.y + 2.7 + i * LABEL_LINE_PITCH, 0.26));
    let yb = ctx.y + 6.8 + labelExtra;                      // next line's baseline
    for (let i = 0; i < lines.length; ) {
      // newPage() leaves the continuation-header font set; restore per chunk.
      setF(doc, 9.4, 'normal', v === '—' ? T.MUTED : T.BODY);
      const fit = Math.max(1, Math.floor((ctx.maxY - yb) / COMMENT_LINE_H) + 1);
      const chunk = lines.slice(i, i + fit);
      chunk.forEach((ln, k) => doc.text(ln, ctx.margin, yb + k * COMMENT_LINE_H));
      i += chunk.length;
      if (i < lines.length) {
        newPage(ctx);
        yb = ctx.y + COMMENT_LINE_H;
      } else {
        // Same trailing room the one-shot path leaves under its last baseline.
        ctx.y = yb + (chunk.length - 1) * COMMENT_LINE_H + 5.1;
      }
    }
    return;
  }

  ensure(ctx, h);
  setF(doc, 7.1, 'mono', T.MUTED);
  labelLines.forEach((ln, i) =>
    tracked(doc, ln, ctx.margin, ctx.y + 2.7 + i * LABEL_LINE_PITCH, 0.26));
  setF(doc, 9.4, 'normal', v === '—' ? T.MUTED : T.BODY);
  doc.text(lines, ctx.margin, ctx.y + 6.8 + labelExtra, { lineHeightFactor: 1.5 });
  ctx.y += h;
}

// 3.7mm rounded box — the 2b .box.on/.box.off treatment. Checked = solid ink
// fill + a hand-vectored yellow check; empty = white fill, sig-line border.
// The check is drawn as strokes, never the '✓' glyph — the WinAnsi text
// guard (TRANSLITERATE, above) turns that codepoint into the word "Yes", so a
// font-glyph check would print as literal text inside the box on a real
// client PDF. Species never changes: this is always a checkbox (see radio()
// below for the circle-and-dot species, restyled the same way).
const CHECK_S = 3.7;
const CHECK_R = 0.8;
export function checkbox(doc, x, y, checked) {
  audit('checkbox');
  if (checked) {
    doc.setFillColor(...T.INK);
    doc.roundedRect(x, y, CHECK_S, CHECK_S, CHECK_R, CHECK_R, 'F');
    doc.setDrawColor(...T.ORDINEX);
    doc.setLineWidth(0.45);
    let capJoinSupported = true;
    try { doc.setLineCap('round'); doc.setLineJoin('round'); }
    catch { capJoinSupported = false; }
    doc.lines([[0.6, 0.7], [1.3, -1.6]], x + 0.9, y + 1.9, [1, 1], 'S', false);
    if (capJoinSupported) { doc.setLineCap('butt'); doc.setLineJoin('miter'); }
  } else {
    doc.setFillColor(...T.WHITE);
    doc.setDrawColor(...T.SIG_LINE);
    doc.setLineWidth(0.4);
    doc.roundedRect(x, y, CHECK_S, CHECK_S, CHECK_R, CHECK_R, 'FD');
  }
}

// Checkbox list in equal columns (default 2) — the 2b .check-grid: no cell
// borders, box + label flush at the column edge, one 6.2mm row per item.
// items: [[label, checked], ...]
export function checkGrid(ctx, items, cols = 2, opts) {
  const { doc } = ctx;
  const rows = Math.ceil(items.length / cols);
  const cw = ctx.W / cols;
  const rowH = 6.2;
  for (let r = 0; r < rows; r++) {
    ensure(ctx, rowH);
    for (let c = 0; c < cols; c++) {
      const it = items[c * rows + r];
      if (!it) continue;
      const x = ctx.margin + c * cw;
      checkbox(doc, x, ctx.y + (rowH - CHECK_S) / 2, it[1]);
      setF(doc, 9.4, 'normal', T.BODY);
      doc.text(String(it[0]), x + CHECK_S + 2.4, ctx.y + rowH / 2 + 1.15);
      // Sheet-editor box map: one rect per printed cell, item-indexed.
      opts?.onCell?.(c * rows + r, { x, y: ctx.y, w: cw - 4, h: rowH });
    }
    // Faint per-column rule under each row (never after the last): long
    // two-column checklists are where the eye loses its line on paper. The
    // 7.4mm gutter keeps the two columns reading as columns, not one table.
    if (r < rows - 1) {
      doc.setDrawColor(...T.HAIR_LT);
      doc.setLineWidth(0.2);
      for (let c = 0; c < cols; c++) {
        if (!items[c * rows + r]) continue;
        const x = ctx.margin + c * cw;
        doc.line(x, ctx.y + rowH - 0.6, x + cw - 7.4, ctx.y + rowH - 0.6);
      }
    }
    ctx.y += rowH;
  }
}

// Circle + dot — restyled colours only. Never becomes a checkbox no matter
// how the checkbox's own visual language shifts (structure rule: species
// never changes).
function radio(doc, x, y, selected) {
  audit('radio');
  doc.setDrawColor(...T.SIG_LINE);
  doc.setLineWidth(0.4);
  doc.circle(x, y, 1.75, 'S');
  if (selected) { doc.setFillColor(...T.INK); doc.circle(x, y, 1, 'F'); }
}

// Question left, Yes/No(/N/A) radio group right-aligned in the same row — no
// box; a hairline under the row closes it instead (the 2b field grammar).
export function ynRow(ctx, question, answer, opts = {}) {
  const { doc } = ctx;
  audit('ynRow');
  const options = opts.withNA === false ? ['Yes', 'No'] : ['Yes', 'No', 'N/A'];
  const optionsW = options.length * 16;
  // A long question wraps inside its own column — same 4.4mm pitch and +3.6
  // breathing room as statusRow — instead of running under the option group.
  setF(doc, 9.4, 'normal', T.BODY);
  const lines = doc.splitTextToSize(String(question ?? ''), ctx.W - optionsW - 6);
  const h = Math.max(8, lines.length * 4.4 + 3.6);
  ensure(ctx, h);
  setF(doc, 9.4, 'normal', T.BODY);
  // Single line keeps the bare text() call: passing the options object emits
  // different (equivalent) stream operators, and the regen diff protocol
  // wants unchanged rows byte-identical.
  if (lines.length === 1) doc.text(lines[0], ctx.margin, ctx.y + 5.1);
  else doc.text(lines, ctx.margin, ctx.y + 5.1, { lineHeightFactor: 1.2 });
  let cx = ctx.margin + ctx.W - optionsW;
  const norm = String(answer ?? '').toLowerCase();
  options.forEach((o) => {
    radio(doc, cx, ctx.y + h / 2, o.toLowerCase() === norm || (o === 'N/A' && norm === 'na'));
    setF(doc, 8.5, 'normal', T.BODY);
    doc.text(o, cx + 3, ctx.y + h / 2 + 1.1);
    cx += 16;
  });
  hline(doc, ctx.margin, ctx.y + h, ctx.W, 0.2, T.HAIR_LT);
  ctx.y += h;
}

// One checklist-field row (Onda 1): item text left, one checkbox per status
// option right-aligned — ynRow's layout with arbitrary options. When the
// options block would eat more than ~55% of the width (many/long labels),
// the item gets its own line and the options render beneath, still one row.
// No box; a hairline under the row closes it (the 2b field grammar).
export function statusRow(ctx, item, options, chosen, comment) {
  const { doc } = ctx;
  audit('statusRow');
  const opts = options || [];
  // Measured at the same size/face the option label is actually drawn at
  // (8.5pt, below) so optW's stacked/unstacked threshold reflects real glyph
  // widths — color doesn't affect getTextWidth, but size does.
  setF(doc, 8.5, 'normal', T.BODY);
  const optW = opts.map(o => doc.getTextWidth(String(o)) + 7.5);
  const blockW = optW.reduce((s, w) => s + w, 0);
  const stacked = blockW > ctx.W * 0.55;
  const itemW = stacked ? ctx.W - 4 : ctx.W - blockW - 6;
  setF(doc, 9.4, 'normal', T.BODY);
  const lines = doc.splitTextToSize(String(item), itemW);
  const note = String(comment || '').trim();
  setF(doc, 7.5, 'normal', T.MUTED);
  const noteLines = note ? doc.splitTextToSize(note, ctx.W - 8) : [];
  const textH = lines.length * 4.4 + 3.6;
  const noteH = noteLines.length ? noteLines.length * 3.8 + 2 : 0;
  const h = (stacked ? textH + 6 : Math.max(8, textH)) + noteH;
  ensure(ctx, h);
  setF(doc, 9.4, 'normal', T.BODY);
  doc.text(lines, ctx.margin + 2, ctx.y + 5.1, { lineHeightFactor: 1.2 });
  const rowH = h - noteH;
  let cx = stacked ? ctx.margin + 2.5 : ctx.margin + ctx.W - blockW - 2;
  const cy = stacked ? ctx.y + rowH - 4.5 : ctx.y + rowH / 2 - 1.75;
  opts.forEach((o, i) => {
    checkbox(doc, cx, cy, String(o) === String(chosen ?? ''));
    setF(doc, 8.5, 'normal', T.BODY);
    doc.text(String(o), cx + 4.5, cy + 2.8);
    cx += optW[i];
  });
  if (noteLines.length) {
    setF(doc, 7.5, 'normal', T.MUTED);
    doc.text(noteLines, ctx.margin + 6, ctx.y + rowH + 2.6, { lineHeightFactor: 1.15 });
  }
  hline(doc, ctx.margin, ctx.y + h, ctx.W, 0.2, T.HAIR_LT);
  ctx.y += h;
}

// Data table (Onda 1 'table' fields): strip-fill header row, wrapped cells,
// equal column widths, zebra body rows, no vertical rules — divisions are
// hairlines only. The header is redrawn after an auto page-break so no page
// starts with orphan data rows.
export function tableGrid(ctx, columns, rows) {
  const { doc } = ctx;
  const cols = columns || [];
  if (!cols.length) return;
  audit('table');
  audit('tableRow', (rows || []).length);
  const cw = ctx.W / cols.length;

  const drawHeader = () => {
    ensure(ctx, 7);
    doc.setFillColor(...T.STRIP);
    doc.rect(ctx.margin, ctx.y, ctx.W, 7, 'F');
    setF(doc, 7.5, 'semi', T.INK);
    cols.forEach((c, i) => {
      doc.text(String(c.label || c.key || '').toUpperCase(), ctx.margin + i * cw + 2, ctx.y + 4.7);
    });
    ctx.y += 7;
    hline(doc, ctx.margin, ctx.y, ctx.W, 0.2, T.HAIR);
  };

  drawHeader();
  (rows || []).forEach((r, idx) => {
    setF(doc, 9, 'normal');
    const cellLines = cols.map(c => doc.splitTextToSize(String((r && r[c.key]) ?? ''), cw - 4));
    const h = Math.max(7, Math.max(...cellLines.map(l => l.length)) * 4.2 + 3.4);
    if (ctx.y + h > ctx.maxY) { newPage(ctx); drawHeader(); }
    if (idx % 2 === 1) {
      doc.setFillColor(...T.HAIR_LT);
      doc.rect(ctx.margin, ctx.y, ctx.W, h, 'F');
    }
    setF(doc, 9, 'normal', T.BODY);
    cols.forEach((c, i) => {
      doc.text(cellLines[i], ctx.margin + i * cw + 2, ctx.y + 4.6, { lineHeightFactor: 1.15 });
    });
    ctx.y += h;
    hline(doc, ctx.margin, ctx.y, ctx.W, 0.2, T.HAIR_LT);
  });
}

// Two-column Hazards | Corrective Actions block — the 2b .two-col: mono caps
// labels (not boxed header cells), grey bullets, no borders anywhere.
const HAZ_GAP    = 6.4;   // 24px column gap
const HAZ_LINE_H = 4.9;   // 9.4pt at line-height 1.5
export function hazardPair(ctx, hazards, actions, opts) {
  const { doc } = ctx;
  audit('hazardPair');
  const cw = (ctx.W - HAZ_GAP) / 2;
  setF(doc, 9.4, 'normal');
  const dotW = doc.getTextWidth('•');
  const indent = dotW + 2.1;                 // 8px bullet→text gap
  // Each list item keeps its own wrapped block so the bullet marks the item,
  // not every line of it.
  const wrapCol = (arr) => (arr || []).map(s =>
    doc.splitTextToSize(String(s ?? ''), cw - indent));
  const cols = [wrapCol(hazards), wrapCol(actions)];
  const lineCount = cols.map(items => items.reduce((n, l) => n + l.length, 0));
  // Same "label at 2.7, body at 6.8, +7 of breathing room" shape commentArea
  // uses, so a hazard block and a text field sit on the same rhythm.
  const h = Math.max(...lineCount) * HAZ_LINE_H + 7;
  ensure(ctx, h);                            // keep-together, as before
  const top = ctx.y;
  ['Hazards', 'Corrective Actions'].forEach((label, c) => {
    const x = ctx.margin + c * (cw + HAZ_GAP);
    setF(doc, 7.1, 'mono', T.MUTED);
    tracked(doc, label.toUpperCase(), x, top + 2.7, 0.26);
    let k = 0;
    cols[c].forEach((itemLines, itemIdx) => {
      const y = top + 6.8 + k * HAZ_LINE_H;
      setF(doc, 9.4, 'normal', T.DOT);
      doc.text('•', x, y);
      setF(doc, 9.4, 'normal', T.BODY);
      doc.text(itemLines, x + indent, y, { lineHeightFactor: 1.5 });
      // Sheet-editor box map: one rect per list item (column 0 = hazards,
      // column 1 = corrective actions).
      opts?.onCell?.(c, itemIdx, { x, y: y - 3.4, w: cw, h: itemLines.length * HAZ_LINE_H });
      k += itemLines.length;
    });
  });
  ctx.y = top + h;
}

// Risk pill — the 2b .badge. Draws at (x, yMid) and returns the width it
// occupied so the caller can lay a task title out around it; an unknown or
// empty level draws nothing and returns 0 (a form with no risk rating must not
// grow a blank pill).
const BADGE_PAD_X = 2.4;    // 9px
const BADGE_PAD_Y = 0.5;    // 2px
const BADGE_TEXT_H = 3.8;   // the reference pill's 19px minus its padding+border
const BADGE_CS = 0.21;      // 0.8px letter-spacing
export function badge(ctx, x, yMid, level) {
  const { doc } = ctx;
  const key = String(level ?? '').trim().toLowerCase();
  // hasOwnProperty, not a bare lookup: a tenant-authored level literally named
  // "constructor" or "toString" would otherwise resolve to an Object prototype
  // member and crash setFillColor on the next line.
  if (!Object.prototype.hasOwnProperty.call(BADGE, key)) return 0;
  const c = BADGE[key];
  const label = key.toUpperCase();
  setF(doc, 7.5, 'mono', c.fg);
  const w = trackedW(doc, label, BADGE_CS) + BADGE_PAD_X * 2;
  const h = BADGE_TEXT_H + BADGE_PAD_Y * 2;
  doc.setFillColor(...c.bg);
  doc.setDrawColor(...c.bd);
  doc.setLineWidth(0.26);
  doc.roundedRect(x, yMid - h / 2, w, h, 1.1, 1.1, 'FD');
  // 7.5pt caps stand ~1.85mm tall; half of that puts them optically centred.
  tracked(doc, label, x + BADGE_PAD_X, yMid + 0.93, BADGE_CS);
  return w;
}

// One crew sign-in line — the 2b .sign-row: number, name, a flexible rule to
// sign on, and a SIGNATURE tag at the far right. A captured signature image
// sits ON the rule (the rule still prints under it, as in the reference).
// The name wraps rather than truncating; the row grows with it and never
// splits across a page.
const SIGN_ROW_H  = 11.6;   // 44px: 12px pad + 22px rule + 10px pad
const SIGN_NAME_W = 74;     // 280px fixed name column
const SIGN_NUM_W  = 4.8;    // 18px
const SIGN_GAP    = 3.7;    // 14px
const SIGN_IMG_H  = 14;
const SIGN_TAG    = 'SIGNATURE';
// Baseline-to-baseline pitch of a wrapped name line, and the lineHeightFactor
// that produces it at 9.75pt — kept as a pair so the height reserved for a
// wrapped name is exactly the height jsPDF draws.
const SIGN_NAME_LINE_H = 4.6;
const SIGN_NAME_LHF    = 1.34;
export function signRow(ctx, n, name, sigDataUrl) {
  const { doc } = ctx;
  audit('signRow');
  const nameX = ctx.margin + SIGN_NUM_W + SIGN_GAP;
  setF(doc, 9.75, 'semi');
  const nameLines = doc.splitTextToSize(String(name ?? ''), SIGN_NAME_W);
  const extra = (nameLines.length - 1) * SIGN_NAME_LINE_H;
  let imgW = 0, imgH = 0;
  if (sigDataUrl) {
    try {
      const pr = doc.getImageProperties(sigDataUrl);
      imgH = SIGN_IMG_H;
      imgW = (pr.width / pr.height) * imgH;
    } catch { imgW = imgH = 0; }
  }
  setF(doc, 6.75, 'mono');
  const tagW = trackedW(doc, SIGN_TAG, 0.26);
  const lineX0 = nameX + SIGN_NAME_W + SIGN_GAP;
  const lineX1 = ctx.margin + ctx.W - tagW - SIGN_GAP;
  const lineW = Math.max(12, lineX1 - lineX0);
  if (imgW > lineW) { imgH *= lineW / imgW; imgW = lineW; }
  // 5mm keeps the tallest signature clear of the row above it.
  const h = Math.max(SIGN_ROW_H, imgH + 5) + extra;
  ensure(ctx, h);
  const lineY = ctx.y + h - 3.4;
  const baseline = lineY - 1.2;               // everything bottom-aligns on the rule
  setF(doc, 8.25, 'mono', T.MUTED2);
  doc.text(String(n), ctx.margin, baseline);
  setF(doc, 9.75, 'semi', T.INK);
  doc.text(nameLines, nameX, baseline - extra, { lineHeightFactor: SIGN_NAME_LHF });
  hline(doc, lineX0, lineY, lineW, 0.25, T.SIG_LINE);
  if (imgW && imgH) {
    try {
      // 'SLOW' re-compresses the PNG stream — a full crew of signatures is the
      // heaviest thing on these PDFs and this costs nothing visible.
      doc.addImage(sigDataUrl, imageFormat(sigDataUrl),
        lineX0 + (lineW - imgW) / 2, lineY - imgH, imgW, imgH, undefined, 'SLOW');
    } catch { /* unreadable signature: the blank rule stands */ }
  }
  setF(doc, 6.75, 'mono', T.MUTED);
  tracked(doc, SIGN_TAG, ctx.margin + ctx.W - tagW, baseline, 0.26);
  hline(doc, ctx.margin, ctx.y + h, ctx.W, 0.2, T.HAIR_LT);
  ctx.y += h;
}

// Word-wraps `text` into `maxW`, with the first line shortened by `firstIndent`
// (room for an inline lead-in drawn in a different face). Measures with the
// font currently set on `doc`; the caller sets that before calling.
function wrapAfterLead(doc, text, maxW, firstIndent) {
  const words = String(text ?? '').split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let line = '';
  let limit = Math.max(1, maxW - firstIndent);
  words.forEach((w) => {
    const candidate = line ? line + ' ' + w : w;
    if (line && doc.getTextWidth(candidate) > limit) {
      lines.push(line);
      line = w;
      limit = maxW;
    } else {
      line = candidate;
    }
  });
  if (line) lines.push(line);
  return lines;
}

// Certification box — the 2b .cert: strip fill, hairline border, the title set
// inline in ink ahead of an em-dash and the body copy. Grows with the text and
// never splits across a page (a certification that reads half on each side of a
// break is worthless in an audit). A certification longer than a whole page
// would overflow the footer rather than lose a word — the tenant-authored
// meta.certification would have to run past ~45 lines for that, and losing the
// text is the worse failure.
const CERT_PAD_X  = 4.8;    // 18px
const CERT_PAD_Y  = 3.7;    // 14px
const CERT_LINE_H = 4.92;   // 9pt at line-height 1.55
// Height certBox will occupy — for sectionBar's keepWith, so a sign-off bar
// and the certification under it break to the next page together instead of
// leaving the bar stranded (same contract as signerH below). Measuring costs
// a text wrap, which certBox does anyway.
export function measureCertBox(ctx, title, text) {
  const { doc } = ctx;
  const innerW = ctx.W - CERT_PAD_X * 2;
  const head = String(title ?? '').trim();
  setF(doc, 9, 'semi');
  const headW = head ? doc.getTextWidth(head) : 0;
  setF(doc, 9, 'normal');
  const dashW = head ? doc.getTextWidth(' — ') : 0;
  const lines = wrapAfterLead(doc, String(text ?? '').trim(), innerW, headW + dashW);
  return Math.max(1, lines.length) * CERT_LINE_H + CERT_PAD_Y * 2;
}

export function certBox(ctx, title, text) {
  const { doc } = ctx;
  audit('certBox');
  const innerW = ctx.W - CERT_PAD_X * 2;
  const head = String(title ?? '').trim();
  setF(doc, 9, 'semi');
  const headW = head ? doc.getTextWidth(head) : 0;
  setF(doc, 9, 'normal');
  const dash = head ? ' — ' : '';
  const dashW = head ? doc.getTextWidth(dash) : 0;
  const body = String(text ?? '').trim();
  const lines = wrapAfterLead(doc, body, innerW, headW + dashW);
  const rows = Math.max(1, lines.length);
  const h = rows * CERT_LINE_H + CERT_PAD_Y * 2;
  ensure(ctx, h);
  doc.setFillColor(...T.STRIP);
  doc.setDrawColor(...T.HAIR);
  doc.setLineWidth(0.25);
  doc.rect(ctx.margin, ctx.y, ctx.W, h, 'FD');
  const x = ctx.margin + CERT_PAD_X;
  const first = ctx.y + CERT_PAD_Y + 3.2;
  if (head) {
    setF(doc, 9, 'semi', T.INK);
    doc.text(head, x, first);
    setF(doc, 9, 'normal', T.SEC);
    // The WinAnsi guard trims each string it draws, so the em-dash separator
    // carries its own spacing as an offset instead of as leading blanks.
    doc.text('—', x + headW + dashW / 2 - doc.getTextWidth('—') / 2, first);
  }
  setF(doc, 9, 'normal', T.SEC);
  if (lines.length) {
    doc.text(lines[0], x + headW + dashW, first);
    if (lines.length > 1) {
      doc.text(lines.slice(1), x, first + CERT_LINE_H, { lineHeightFactor: 1.55 });
    }
  }
  ctx.y += h;
}

// One photo row's image dims + cell height — shared by photoGrid and the
// keep-with measurement photoRowH exposes, so the two can never drift.
const PHOTO_MAX_H = 55;
function photoRowDims(doc, pair, cw) {
  const dims = pair.map(p => {
    try {
      const pr = doc.getImageProperties(p.dataUrl);
      const w = Math.min(cw - 8, (pr.width / pr.height) * PHOTO_MAX_H);
      const h = Math.min(PHOTO_MAX_H, (pr.height / pr.width) * (cw - 8));
      return { w, h };
    } catch { return { w: cw - 8, h: 40 }; }
  });
  return { dims, cellH: Math.max(...dims.map(d => d.h)) + 9 };
}

// Height the first photo row will occupy — for sectionBar's keepWith, so a
// PHOTOS bar is never stranded alone at a page bottom with its first row
// overleaf (the bar's own 10mm lookahead is far under a 64mm photo row).
export function photoRowH(ctx, photos) {
  if (!photos || !photos.length) return 0;
  return photoRowDims(ctx.doc, photos.slice(0, 2), ctx.W / 2).cellH;
}

// Photo grid, 2 per row, captions under each. photos: [{dataUrl, caption}].
// Same grid and sizing as before — only the frame and the caption moved onto
// the 2b palette (hairline cell, mono caps-scale caption).
export function photoGrid(ctx, photos) {
  const { doc } = ctx;
  audit('photo', photos.length);
  const cw = ctx.W / 2;
  for (let i = 0; i < photos.length; i += 2) {
    const pair = photos.slice(i, i + 2);
    const { dims, cellH } = photoRowDims(doc, pair, cw);
    ensure(ctx, cellH);
    pair.forEach((p, j) => {
      const x = ctx.margin + j * cw;
      doc.setDrawColor(...T.HAIR);
      doc.setLineWidth(0.2);
      doc.rect(x, ctx.y, cw, cellH, 'S');
      try {
        doc.addImage(p.dataUrl, imageFormat(p.dataUrl),
          x + (cw - dims[j].w) / 2, ctx.y + 2, dims[j].w, dims[j].h - 2, undefined, 'FAST');
      } catch { /* unreadable photo: leave the cell empty */ }
      if (p.caption) {
        setF(doc, 6.75, 'mono', T.MUTED);
        doc.text(String(p.caption), x + cw / 2, ctx.y + cellH - 2.5, { align: 'center' });
      }
    });
    ctx.y += cellH;
  }
}

// Sign-off block — the 2b .signoff-grid: SIGNED BY over "name — role", the
// date in mono at the right, then a SIGNATURE label over a 60mm rule with the
// captured signature image sitting on it. The image is the species here (a
// SignaturePad capture) and never degrades to a printed name. A signer's
// identity block and their signature never split across pages.
// signers: [{name, role, when, sig}] — sig is a dataURL or null (blank rule
// for on-paper signing).
const SIGNOFF_ID_H     = 9;
const SIGNOFF_LINE_W   = 60;
const SIGNOFF_IMG_H    = 16;
const SIGNOFF_WRITE_H  = 6.9;   // 26px of on-paper writing room, per the reference

// One signer's measurements — shared by signatureBlock and the keep-with
// measurement signerH exposes, so the two can never drift. Measure the
// signature before reserving space: the rule hugs its label on a blank
// (sign-on-paper) block and only opens up for a real capture.
function signoffDims(doc, s) {
  let imgW = 0, imgH = 0;
  if (s.sig) {
    try {
      const pr = doc.getImageProperties(s.sig);
      imgH = Math.min(SIGNOFF_IMG_H,
        ((pr.height / pr.width) * SIGNOFF_LINE_W) || SIGNOFF_IMG_H);
      imgW = Math.min(SIGNOFF_LINE_W, (pr.width / pr.height) * imgH);
    } catch { imgW = imgH = 0; }
  }
  const sigH = 4 + Math.max(SIGNOFF_WRITE_H, imgH + 1) + 4;
  return { imgW, imgH, sigH, total: SIGNOFF_ID_H + sigH };
}

// Height signatureBlock will reserve for one signer — for sectionBar's
// keepWith, so a sign-off bar and its first signer page-break together
// instead of stranding the bar alone at a page bottom.
export function signerH(ctx, signer) {
  return signoffDims(ctx.doc, signer || {}).total;
}

export function signatureBlock(ctx, signers) {
  const { doc } = ctx;
  audit('signature', signers.length);
  signers.forEach((s, i) => {
    const { imgW, imgH, sigH } = signoffDims(doc, s);
    ensure(ctx, SIGNOFF_ID_H + sigH);      // identity + signature never split
    const top = ctx.y;
    setF(doc, 7.1, 'mono', T.MUTED);
    tracked(doc, 'SIGNED BY', ctx.margin, top + 2.7, 0.26);
    setF(doc, 9.75, 'semi', T.INK);
    doc.text([s.name, s.role].filter(Boolean).join(' — '), ctx.margin, top + 6.8);
    if (s.when) {
      setF(doc, 8.25, 'mono', T.MUTED2);
      doc.text(String(s.when), ctx.margin + ctx.W, top + 6.8, { align: 'right' });
    }
    ctx.y += SIGNOFF_ID_H;
    const lineY = ctx.y + sigH - 4;
    setF(doc, 7.1, 'mono', T.MUTED);
    tracked(doc, 'SIGNATURE', ctx.margin, ctx.y + 2.7, 0.26);
    if (imgW && imgH) {
      try {
        // 'SLOW' re-compresses the PNG stream: a SignaturePad capture is the
        // heaviest object on most of these PDFs and this costs nothing visible.
        doc.addImage(s.sig, imageFormat(s.sig),
          ctx.margin + (SIGNOFF_LINE_W - imgW) / 2, lineY - imgH, imgW, imgH,
          undefined, 'SLOW');
      } catch { /* unreadable signature: the blank rule stands */ }
    }
    hline(doc, ctx.margin, lineY, SIGNOFF_LINE_W, 0.25, T.SIG_LINE);
    ctx.y += sigH;
    if (i < signers.length - 1) {
      hline(doc, ctx.margin, ctx.y, ctx.W, 0.2, T.HAIR_LT);
    }
  });
}

// Right-aligned bold total under a section box.
export function totalLine(ctx, text) {
  audit('total');
  ensure(ctx, 6);
  setF(ctx.doc, 9.75, 'bold', T.INK);
  ctx.doc.text(text, ctx.margin + ctx.W, ctx.y + 4, { align: 'right' });
  ctx.y += 6;
}

// Footer on every page: document name left, the Ordinex signature centre,
// pagination right. Call once, after all content.
// The centre block only prints when brand.poweredBy is set — orgs on the
// white-label flag (features.hide_powered_by) get an empty string and the
// footer stays two-part.
export function finalize(ctx) {
  const { doc } = ctx;
  const total = doc.getNumberOfPages();
  // ctx.pageFooters (optional, by page index) names what each page holds when
  // the pages of one document differ — one Production Report page per item.
  const docLeft = ctx.title + (ctx.docId ? ' · ' + ctx.docId : '');
  const powered = String(ctx.brand.poweredBy || '');
  // "Powered by Ordinex — app.ordinex.ca": the product name is set in ink and
  // semibold, the rest muted. Split rather than hardcoded so a different
  // string still prints in full.
  const at = powered.indexOf('Ordinex');
  const pre = at >= 0 ? powered.slice(0, at) : powered;
  const mid = at >= 0 ? 'Ordinex' : '';
  // The WinAnsi guard trims each string it draws, so a segment that starts
  // with a space would lose it and " — app.ordinex.ca" would butt against the
  // product name. Carry that space as an explicit offset instead.
  const postRaw = at >= 0 ? powered.slice(at + 7) : '';
  const post = postRaw.trim();

  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    const left = ctx.pageFooters?.[p - 1] || docLeft;
    const ruleY = ctx.pageH - FOOTER_RULE;
    // Descenders must clear the printer's non-printable edge (~6.35mm on most
    // office laser trays); a 6.5mm baseline put them right in it.
    const ty = ctx.pageH - 8.8;
    hline(doc, ctx.margin, ruleY, ctx.W, 0.2, T.HAIR);

    let centerStart = ctx.pageW / 2;
    if (powered) {
      setF(doc, 7.5, 'normal', T.MUTED);
      const preW = doc.getTextWidth(pre);
      const postPad = /^\s/.test(postRaw) ? doc.getTextWidth(' ') : 0;
      const postW = post ? doc.getTextWidth(post) + postPad : 0;
      setF(doc, 7.5, 'semi', T.INK);
      const midW = mid ? doc.getTextWidth(mid) : 0;
      const totalW = 1.85 + 1.6 + preW + midW + postW;
      let x = ctx.pageW / 2 - totalW / 2;
      centerStart = x;
      doc.setFillColor(...T.ORDINEX);
      doc.rect(x, ty - 1.85, 1.85, 1.85, 'F');
      x += 1.85 + 1.6;
      setF(doc, 7.5, 'normal', T.MUTED);
      doc.text(pre, x, ty);
      x += preW;
      if (mid) {
        setF(doc, 7.5, 'semi', T.INK);
        doc.text(mid, x, ty);
        x += midW;
      }
      if (post) {
        setF(doc, 7.5, 'normal', T.MUTED);
        doc.text(post, x + postPad, ty);
      }
    }

    // A long document title must never run into the signature block.
    let leftSize = 7.5, leftTxt = left;
    setF(doc, leftSize, 'normal', T.MUTED);
    const room = centerStart - ctx.margin - 3;
    while (leftSize > 6 && doc.getTextWidth(leftTxt) > room) {
      leftSize -= 0.25;
      setF(doc, leftSize, 'normal', T.MUTED);
    }
    // Still too long at 6pt: cut it, rather than print over "Powered by".
    if (doc.getTextWidth(leftTxt) > room) {
      while (leftTxt.length > 1 && doc.getTextWidth(leftTxt + '…') > room) leftTxt = leftTxt.slice(0, -1);
      leftTxt = leftTxt.trimEnd() + '…';
    }
    doc.text(leftTxt, ctx.margin, ty);

    setF(doc, 7.5, 'mono', T.MUTED);
    doc.text(`Page ${p} of ${total}`, ctx.margin + ctx.W, ty, { align: 'right' });
  }
}
